import React from 'react'
import { Container, Box, Typography, CircularProgress } from '@mui/material'
import { Slide, Fade } from 'react-awesome-reveal'
import { useConfig } from '../../hooks/useConfig'
import {
    WelcomeAnimation,
    TechnicalAnimation,
    LeadershipAnimation,
    CreativeAnimation,
} from './animations'

const Home: React.FC = () => {
    const { config, loading } = useConfig()

    if (loading || !config) {
        return (
            <Container
                maxWidth="lg"
                sx={{
                    py: 8,
                    minHeight: '100vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <CircularProgress />
            </Container>
        )
    }

    const contentSections = config.home.sections

    const getAnimationComponent = (index: number) => {
        switch (index) {
            case 0:
                return <WelcomeAnimation />
            case 1:
                return <TechnicalAnimation />
            case 2:
                return <LeadershipAnimation />
            case 3:
                return <CreativeAnimation />
            default:
                return <WelcomeAnimation />
        }
    }

    return (
        <Container
            maxWidth="lg"
            sx={{
                py: { xs: 2, sm: 4 },
                minHeight: '100vh',
            }}
        >
            {contentSections.map((section, index) => (
                <Box
                    key={index}
                    sx={{
                        minHeight: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        textAlign: 'center',
                        py: index === 0 ? { xs: 2, sm: 4 } : 8,
                        px: 2,
                    }}
                >
                    <Fade triggerOnce>
                        <Slide
                            direction={
                                index === 0
                                    ? 'down'
                                    : index % 2 === 0
                                      ? 'left'
                                      : 'right'
                            }
                            triggerOnce
                            duration={index === 0 ? 800 : undefined}
                        >
                            <Typography
                                variant="h1"
                                component="h1"
                                sx={{
                                    fontSize: { xs: '2.5rem', md: '4rem' },
                                    fontWeight: 700,
                                    mb: 4,
                                    color: 'primary.main',
                                }}
                            >
                                {section.title}
                            </Typography>
                        </Slide>
                    </Fade>

                    <Fade delay={200} triggerOnce>
                        <Typography
                            variant="h6"
                            component="p"
                            sx={{
                                fontSize: { xs: '1rem', md: '1.25rem' },
                                maxWidth: '800px',
                                mb: 6,
                                lineHeight: 1.8,
                                color: 'text.secondary',
                            }}
                        >
                            {section.description}
                        </Typography>
                    </Fade>

                    <Fade delay={400} triggerOnce>
                        <Box sx={{ width: '100%', display: 'block' }}>
                            <Slide direction="up" delay={400} triggerOnce>
                                {getAnimationComponent(index)}
                            </Slide>
                        </Box>
                    </Fade>
                </Box>
            ))}
        </Container>
    )
}

export default Home
